import {Component, computed, input, output} from '@angular/core';
import {EDITOR_ACCENT, FloorArea, FloorTable, MIN_TABLE_SIZE, TableShape} from '../shared/models/table-layout.model';
import {areaOfTable} from '../shared/models/floor-plan-geometry';

/** Fewest seats a table may be dialled down to — the handover's own stepper floor. */
const MIN_SEATS = 1;

/** Most seats the stepper offers — the handover's own ceiling. */
const MAX_SEATS = 20;

/**
 * The **body** of the floor-plan inspector while a table is selected (#201) — a literal port of the design
 * handover's `insp.isTable` branch: *Form*, *Plätze*, *Größe* and the read-only *Bereich* row, in that order.
 *
 * The panel around it (icon, kind label, name field, close button, Duplizieren/Löschen) belongs to the host screen,
 * because the handover draws one inspector whose *body* swaps with the selection — not three separate cards.
 *
 * Purely controlled: it never mutates the table it is given, it emits a **patched copy** on every change and the
 * host applies it to its draft.
 *
 * `areaId` is **read-only**, and deliberately so: since #201's editor rework the area is *auto-matched* from the
 * table's position (the polygon whose outline contains its centre, the handover's §6), so offering a picker would
 * let the admin choose an area the geometry contradicts. It is shown as a live, coloured *Bereich* row instead,
 * which updates on every pixel of a drag.
 */
@Component({
  selector: 'app-table-editor',
  standalone: true,
  imports: [],
  styleUrl: './floor-plan-editor.scss',
  styles: [`
    /* The inspector body is a column of sections; the host's panel supplies the padding and the same 16 px gap
       to the Duplizieren/Löschen row underneath. */
    :host {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .tp-shapes {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }

    .tp-shape {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      height: 44px;
      border-radius: 11px;
      font-size: 13.5px;
      font-weight: 600;
      background: #f6f5f3;
      color: #6b6f78;
      box-shadow: inset 0 0 0 1px #eceae7;
    }

    .tp-shape.is-active {
      background: #fdf1f7;
      color: #c50e6c;
      box-shadow: inset 0 0 0 1.6px #c50e6c;
    }

    .tp-seats-row {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .tp-seats-label {
      flex: 1;
    }

    .tp-seats-label > :first-child {
      font-size: 13.5px;
      font-weight: 600;
    }

    .tp-seats-label > :last-child {
      font-size: 12.5px;
      /* #6b6b70, not the handover's #a0a0a4: that grey is only 2.61:1 on the white inspector card and fails
         WCAG AA; this one clears it at 5.30:1. Matches .tp-brand-sub / .tp-note and the Offene-Tische
         screen — one accessible secondary grey across the module (#205). */
      color: #6b6b70;
    }

    .tp-stepper {
      display: flex;
      align-items: center;
      gap: 2px;
      background: #f0efed;
      border-radius: 999px;
      padding: 3px;
    }

    .tp-stepper button {
      width: 34px;
      height: 34px;
      border-radius: 999px;
      background: #fff;
      box-shadow: 0 1px 3px rgba(0, 0, 0, .1);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #3d3d40;
    }

    .tp-stepper button:disabled {
      opacity: .35;
      cursor: not-allowed;
    }

    .tp-stepper-value {
      min-width: 34px;
      text-align: center;
      font-size: 16px;
      font-weight: 700;
    }

    .tp-size-row {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .tp-size-row label {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .tp-size-row label > span {
      font-size: 11.5px;
      /* #6b6b70, not the handover's #a0a0a4: on the white inspector card that grey is 2.61:1 and fails WCAG AA;
         this one clears it at 5.30:1. Matches .tp-brand-sub / .tp-note (#205). */
      color: #6b6b70;
    }

    .tp-area-row {
      display: flex;
      align-items: center;
      gap: 10px;
      background: #f6f5f3;
      border-radius: 11px;
      padding: 11px 13px;
    }

    .tp-area-row > .tp-area-key {
      font-size: 13.5px;
      font-weight: 600;
      flex: 0 0 auto;
    }

    .tp-area-value {
      margin-left: auto;
      display: inline-flex;
      align-items: center;
      gap: 7px;
      font-size: 13.5px;
      font-weight: 600;
      color: #1c1c1e;
      min-width: 0;
    }

    .tp-area-value > span:last-child {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .tp-area-dot {
      width: 10px;
      height: 10px;
      border-radius: 999px;
      flex: 0 0 auto;
    }

    .tp-area-empty {
      margin-left: auto;
      font-size: 13px;
      font-weight: 500;
      /* #6b6b70, not the handover's #a0a0a4: on the .tp-area-row's own #f6f5f3 surface that grey is 2.39:1 and
         fails WCAG AA; this one clears it at 4.86:1. Matches .tp-brand-sub / .tp-note (#205). */
      color: #6b6b70;
    }
  `],
  template: `
    @if (table(); as t) {
      <div>
        <div class="tp-section-title">Form</div>
        <div class="tp-shapes" role="group" aria-label="Form">
          @for (option of shapes; track option.value) {
            <button type="button" class="cq-btn tp-shape" [class.is-active]="t.tableShape === option.value"
                    [attr.aria-pressed]="t.tableShape === option.value"
                    (click)="patch({tableShape: option.value})">
              @if (option.value === TableShape.Circle) {
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                     aria-hidden="true"><circle cx="12" cy="12" r="6.5"/></svg>
              } @else {
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                     aria-hidden="true"><rect x="5.5" y="5.5" width="13" height="13" rx="2.5"/></svg>
              }
              {{ option.label }}
            </button>
          }
        </div>
      </div>

      <div class="tp-seats-row">
        <div class="tp-seats-label">
          <div id="table-seats-label">Plätze</div>
          <div>Personen am Tisch</div>
        </div>
        <div class="tp-stepper">
          <button type="button" class="cq-btn" (click)="stepSeats(-1)" [disabled]="t.seats <= minSeats"
                  aria-label="Ein Platz weniger">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"
                 stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"/></svg>
          </button>
          <span class="tp-stepper-value" role="status" aria-labelledby="table-seats-label">{{ t.seats }}</span>
          <button type="button" class="cq-btn" (click)="stepSeats(1)" [disabled]="t.seats >= maxSeats"
                  aria-label="Ein Platz mehr">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"
                 stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
          </button>
        </div>
      </div>

      <div>
        <div class="tp-section-title">Größe <span class="tp-section-muted">(cm im Raster)</span></div>
        <div class="tp-size-row">
          <label>
            <span>Breite</span>
            <input class="cq-in" type="number" [attr.min]="minSize" [value]="round(t.width)"
                   (input)="resize('width', $event)"/>
          </label>
          <label>
            <span>Höhe</span>
            <input class="cq-in" type="number" [attr.min]="minSize" [value]="round(t.height)"
                   (input)="resize('height', $event)"/>
          </label>
        </div>
      </div>

      <!-- Bereich is DERIVED, not chosen: the table belongs to whichever area's polygon contains its centre, and
           the host re-resolves that on every pixel of a drag — so this row updates live while the marker moves. -->
      <div class="tp-area-row" role="status">
        <span class="tp-area-key">Bereich</span>
        @if (matchedArea(); as area) {
          <span class="tp-area-value">
            <span class="tp-area-dot" aria-hidden="true" [style.background]="area.color"></span>
            <span>{{ area.name || '–' }}</span>
          </span>
        } @else {
          <span class="tp-area-empty">keinem Bereich zugeordnet</span>
        }
      </div>
    }
  `
})
export class TableEditorComponent {
  /** The selected table, or `null` when nothing is selected. */
  readonly table = input<FloorTable | null>(null);
  /** The shard's areas — the pool the *Bereich* row is resolved against. */
  readonly areas = input<readonly FloorArea[]>([]);

  /** Emitted with the full, patched table on every edit. */
  readonly tableChange = output<FloorTable>();

  protected readonly TableShape = TableShape;
  protected readonly accent = EDITOR_ACCENT;
  protected readonly minSize = MIN_TABLE_SIZE;
  protected readonly minSeats = MIN_SEATS;
  protected readonly maxSeats = MAX_SEATS;

  /** The handover's own two shapes and their wording (§4). */
  protected readonly shapes: {value: TableShape; label: string}[] = [
    {value: TableShape.Circle, label: 'Rund'},
    {value: TableShape.Rectangle, label: 'Quadrat'}
  ];

  /**
   * The area the selected table currently sits in, resolved **live** from the geometry rather than read off
   * `table.areaId`.
   *
   * Reading the geometry is what makes the row correct mid-drag: the host also writes the resolved id onto the
   * draft (which is what gets persisted), but that write and this display both derive from the same point-in-polygon
   * test, so the row can never disagree with what will be saved.
   */
  protected readonly matchedArea = computed(() => {
    const table = this.table();
    return table ? areaOfTable(this.areas(), table) : null;
  });

  /** Emits the selected table with {@link changes} applied. */
  patch(changes: Partial<FloorTable>): void {
    const current = this.table();
    if (!current) return;
    this.tableChange.emit({...current, ...changes});
  }

  /** Dials the seat count by one, clamped to the stepper's own range. */
  stepSeats(delta: number): void {
    const current = this.table();
    if (!current) return;
    this.patch({seats: Math.min(MAX_SEATS, Math.max(MIN_SEATS, (current.seats || 0) + delta))});
  }

  /**
   * Applies one of the two size fields, clamped to the minimum footprint so a cleared field cannot produce an
   * invisible marker.
   */
  resize(key: 'width' | 'height', event: Event): void {
    const current = this.table();
    if (!current) return;
    const parsed = Number.parseInt((event.target as HTMLInputElement).value, 10);
    this.patch({[key]: Math.max(MIN_TABLE_SIZE, Number.isFinite(parsed) ? parsed : MIN_TABLE_SIZE)});
  }

  protected round(value: number): number {
    return Math.round(value);
  }
}
